import { useApolloClient } from "@apollo/client";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useField } from "react-final-form";
import { useComboboxStore } from "swash/v2/Combobox";
import { RemoteSelect, useRemoteSelectState } from "swash/v2/RemoteSelect";

import { DragInitializer } from "@/components/DraggableList";
import { DroppableCapsuleList } from "@/components/DroppableCapsuleList";
import { ModernCapsuleCounter } from "@/components/ModernCapsuleCounter";
import { ModernCapsuleLimit } from "@/components/ModernCapsuleLimit";
import { useSafeQuery } from "@/containers/Apollo";
import { ModernDraggableCapsule } from "@/containers/ModernDraggableCapsule";
import {
  useDragIndex,
  useDragLimit,
} from "@/containers/routes/home-edition/DragContext";
import dndManager from "@/services/dndManager";
import { moveItems } from "@/services/utils";

const ContentItem = ({
  droppableId,
  index,
  item,
  limit = 10,
  labelSelector,
  onRemove,
  disabled,
  multi,
}) => {
  const draggableId = `${item.id}`;
  const dragIndex = useDragIndex({ index, draggableId, droppableId });
  const dragLimit = useDragLimit({ draggableId, dragIndex, limit });

  if (!item) return null;

  return (
    <ModernDraggableCapsule
      key={draggableId}
      draggableId={draggableId}
      index={index}
    >
      {multi && (
        <ModernCapsuleCounter disabled={disabled} dragIndicator>
          {dragIndex + 1}
        </ModernCapsuleCounter>
      )}
      {labelSelector({ item, onRemove, disabled, dragIndex })}
      {dragLimit && <ModernCapsuleLimit data-position={dragLimit.position} />}
    </ModernDraggableCapsule>
  );
};

const DraggableCapsuleList = memo(
  ({
    name,
    disabled,
    data,
    limit,
    onRemove,
    contentIds,
    labelSelector,
    multi,
  }) => {
    if (!data.length) return null;
    return (
      <DragInitializer>
        <DroppableCapsuleList droppableId={name} disabled={disabled}>
          {[...data]
            .filter(({ id }) => contentIds.includes(id))
            .sort((a, b) => contentIds.indexOf(a.id) - contentIds.indexOf(b.id))
            .map((item, index) => (
              <ContentItem
                key={item.id}
                index={index}
                item={item}
                onRemove={onRemove}
                disabled={disabled}
                limit={limit}
                labelSelector={labelSelector}
                multi={multi}
                droppableId={`${name}-${item.id}`}
              />
            ))}
        </DroppableCapsuleList>
      </DragInitializer>
    );
  },
);

const useResourceSelectState = ({
  name,
  onChange,
  value,
  label,
  query,
  searchVariables,
  model,
  fragment,
  labelElementSelector,
}) => {
  const client = useApolloClient();

  const combobox = useComboboxStore();
  const search = combobox.useState("value");

  const field = useField(name);
  const { onChange: onInputChange } = field.input;

  const { data } = useSafeQuery(query, {
    variables: {
      limit: 20,
      filters: {
        search,
        ...searchVariables,
      },
    },
  });

  const items = useMemo(
    () =>
      data?.connection.nodes.filter(
        (item) => !value.map(({ id }) => id).includes(item.id),
      ) ?? [],
    [data, value],
  );

  const remoteData = useMemo(
    () => ({
      items,
      hasMore: false,
      totalCount: items.length,
    }),
    [items],
  );

  const select = useRemoteSelectState({
    title: label,
    data: remoteData,
    combobox,
    value: null,
    onChange: (item) => {
      if (!item) {
        onInputChange(null);
        return;
      }
      onChange([...value, { id: item.id }]);
    },
    labelSelector: (item) => (
      <div className="pointer-events-none flex w-64">
        {labelElementSelector(item)}
      </div>
    ),
    valueSelector: (item) => item.id?.toString(),
    getItem: (id) => {
      const value = client.readFragment({
        id: `${model}:${id}`,
        fragment,
        fragmentName: fragment.definitions[0].name.value,
      });
      if (!value) {
        throw new Error(`Item not found`);
      }
      return value;
    },
  });

  return select;
};

const ResourceSelect = ({
  placeholder,
  name,
  label,
  onChange,
  value,
  disabled,
  query,
  model,
  multi,
  limit,
  fragment,
  labelElementSelector,
  searchVariables,
}) => {
  const select = useResourceSelectState({
    name,
    onChange,
    value,
    query,
    model,
    fragment,
    label,
    labelElementSelector,
    searchVariables,
  });

  const searchFieldVisible = multi
    ? Boolean(!limit) || value.length < limit
    : value.length < 1;

  if (!searchFieldVisible) return null;

  return (
    <RemoteSelect
      state={select}
      disabled={disabled}
      aria-label={label}
      scale="lg"
      placeholder={placeholder}
    />
  );
};

export const ResourceDraggableField = memo(
  ({
    value,
    placeholder,
    name,
    label,
    onChange,
    limit,
    multi,
    disabled,
    query,
    model,
    fragment,
    searchVariables,
    labelSelector,
    labelElementSelector,
  }) => {
    const contentIds = useMemo(() => value.map(({ id }) => id), [value]);
    const { data, loading, previousData } = useSafeQuery(query, {
      variables: {
        limit: 100,
        filters: {
          id: { in: contentIds },
        },
      },
      skip: !value.length,
    });
    const content = useMemo(() => {
      if (loading) return previousData?.connection.nodes ?? [];
      return data?.connection.nodes ?? [];
    }, [data, previousData, loading]);

    const moveContent = useCallback(
      (from, to) => {
        onChange(moveItems(from, to, value));
      },
      [onChange, value],
    );

    const deleteContent = useCallback(
      (contentIndex) => {
        onChange(value.filter((_, index) => index !== contentIndex));
      },
      [onChange, value],
    );

    const onDragEnd = useCallback(
      (dropResult) => {
        if (!dropResult.destination || dropResult.source.droppableId !== name)
          return;
        moveContent(dropResult.source.index, dropResult.destination.index);
      },
      [moveContent, name],
    );

    useEffect(() => {
      dndManager.addListener(`relatedItem:${name}`, {
        onDragEnd,
      });
      return () => dndManager.removeListener(`relatedItem:${name}`);
    }, [moveContent, onDragEnd, name]);

    return (
      <>
        <DraggableCapsuleList
          name={name}
          disabled={disabled}
          data={content}
          limit={limit}
          onRemove={deleteContent}
          contentIds={contentIds}
          multi={multi}
          labelSelector={labelSelector}
        />
        <ResourceSelect
          placeholder={placeholder}
          name={name}
          label={label}
          onChange={onChange}
          value={value}
          disabled={disabled}
          query={query}
          model={model}
          multi={multi}
          limit={limit}
          fragment={fragment}
          searchVariables={searchVariables}
          labelElementSelector={labelElementSelector}
        />
      </>
    );
  },
);
